import { useHealth } from '../queries';
import { Badge, Button, Card, LoadingBlock, StateBlock } from '../ui';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line/60 py-2 last:border-b-0">
      <dt className="text-xs tracking-wide text-ink-faint uppercase">{label}</dt>
      <dd className="text-right text-sm text-ink">{value}</dd>
    </div>
  );
}

function formatTime(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function HealthPage() {
  const { data, isPending, isError, error, refetch, isFetching } = useHealth();

  const canary = data?.canary;
  const healthy = data?.status === 'healthy';

  return (
    <div className="space-y-6">
      <Card
        title="API health"
        description="The API creates and destroys a real lobby to prove the system works."
        actions={
          <Button size="sm" loading={isFetching} onClick={() => void refetch()}>
            Check now
          </Button>
        }
      >
        {isPending ? (
          <LoadingBlock label="Checking…" />
        ) : isError ? (
          <StateBlock
            tone="bad"
            title="The API did not answer"
            detail={(error as Error).message}
            action={
              <Button size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        ) : (
          <dl>
            <Row
              label="Status"
              value={<Badge tone={healthy ? 'ok' : 'bad'}>{data.status}</Badge>}
            />
            <Row label="Service" value={data.service} />
            {canary && (
              <>
                <Row
                  label="Probe"
                  value={
                    canary.skipped ? (
                      <Badge tone="neutral">skipped</Badge>
                    ) : canary.status === 'healthy' ? (
                      <Badge tone="ok">passed</Badge>
                    ) : (
                      <Badge tone="bad">failed</Badge>
                    )
                  }
                />
                <Row
                  label="Freshness"
                  value={
                    canary.stale ? (
                      <Badge tone="warn">stale, re-checking</Badge>
                    ) : canary.cached ? (
                      <Badge tone="neutral">cached</Badge>
                    ) : (
                      <Badge tone="ok">just measured</Badge>
                    )
                  }
                />
                <Row label="Checked at" value={formatTime(canary.checked_at)} />
                <Row label="Valid until" value={formatTime(canary.expires_at)} />
                {canary.duration_ms !== undefined && (
                  <Row label="Probe duration" value={`${canary.duration_ms} ms`} />
                )}
                {canary.bot_id && <Row label="Bot" value={canary.bot_id} />}
                {canary.note && <Row label="Note" value={canary.note} />}
                {canary.error && (
                  <Row label="Error" value={<span className="text-bad">{canary.error}</span>} />
                )}
              </>
            )}
          </dl>
        )}
      </Card>
    </div>
  );
}
